import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Ban,
  CalendarCheck,
  CalendarClock,
  Check,
  CircleCheck,
  Copy,
  Mail,
  PhoneMissed,
  StickyNote,
  UserX,
  UsersRound,
  Video,
} from "lucide-react";
import {
  useDataProvider,
  useNotify,
  useRefresh,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import type { NevoraDataProvider } from "../datos/dataProvider";
import { useAjustes } from "../datos/useAjustes";
import { useMiRol } from "../datos/useMiRol";
import { useResponsables } from "../datos/useResponsables";
import { claveEtapa } from "../dominio/etapas";
import { aFechaISO } from "../dominio/fechas";
import { MAX_NO_SHOWS } from "../dominio/resultado";
import type { Etapa, Negocio } from "../dominio/tipos";
import { deDatetimeLocal, formatoFecha, formatoFechaHora } from "./formato";
import { SelectorHuecos } from "./SelectorHuecos";

/** 39: tras «Se hizo», la etapa siguiente (Robert: el 99 % de las veces, «Esperando info»). */
const TRAS_CONSULTORIA: Etapa[] = ["esperando_info", "preparando_propuesta"];

/** ¿La base ya tiene el paquete de la 36? La ficha trae `no_shows`. */
const con36 = (negocio: object) => "no_shows" in negocio;

const EN_CONSULTORIA = ["consultoria_agendada", "no_show"];

/** ¿La base ya tiene la 42 (el consultor)? La ficha trae `consultoria_sin_respuesta_at`. */
const con42 = (negocio: object) => "consultoria_sin_respuesta_at" in negocio;

/** 42: canales de la nota del consultor. */
const CANALES_NOTA = ["whatsapp", "llamada", "email", "otro"] as const;

/**
 * Marcas de la cabecera (36): «Cita confirmada» y cuántas veces no se presentó (de 3).
 */
export const MarcasConsultoria = ({ negocio }: { negocio: Negocio }) => {
  const translate = useTranslate();
  if (!con36(negocio)) return null;
  const noShows = negocio.no_shows ?? 0;
  return (
    <>
      {negocio.etapa === "consultoria_agendada" &&
      negocio.consultoria_confirmada_at ? (
        <span
          className="inline-flex items-center gap-1 rounded-full border border-emerald-600 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:text-emerald-300"
          data-testid="cita-confirmada"
        >
          <CalendarCheck className="size-3.5" />
          {translate("nevora.ficha.cita_confirmada")}
        </span>
      ) : null}
      {negocio.etapa === "consultoria_agendada" &&
      negocio.consultoria_sin_respuesta_at ? (
        <span
          className="inline-flex items-center gap-1 rounded-full border border-amber-600 px-2 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-300"
          data-testid="sin-respuesta"
        >
          <PhoneMissed className="size-3.5" />
          {translate("nevora.ficha.sin_respuesta")}
        </span>
      ) : null}
      {noShows > 0 && negocio.embudo === "ventas" ? (
        <span
          className="text-xs font-semibold text-amber-800 dark:text-amber-300"
          data-testid="no-shows"
        >
          {translate("nevora.ficha.no_shows", {
            n: noShows,
            max: MAX_NO_SHOWS,
          })}
        </span>
      ) : null}
    </>
  );
};

/**
 * Bloque de la consultoría en la ficha (36, Robert 20/10/2026): quién la hace (uno o varios; el
 * primero se queda la ficha cuando esté hecha). 39 (Robert, 23/10/2026): quién la agendó (para que
 * el consultor pueda escribirle); quién la hace lo eligen Jan y Robert; «Marcar cita confirmada» y
 * el resultado («Se hizo» / «No se presentó») son de quien la hace (o de Jan y Robert).
 */
export const ConsultoriaFicha = ({
  negocio,
  tocable,
}: {
  negocio: Negocio;
  /** El usuario puede trabajar la tarjeta (dueño en sus etapas, o Jan y Robert). */
  tocable: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const { id: yo, esAdmin } = useMiRol();
  const { nombre } = useResponsables();
  const { ajustes } = useAjustes();
  const [editando, setEditando] = useState(false);
  const [eleccion, setEleccion] = useState<Identifier[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [resultado, setResultado] = useState<
    "se_hizo" | "no_se_presento" | null
  >(null);
  // 41: cambiar la hora entre los huecos libres (con el calendario propio).
  const [cambiandoHora, setCambiandoHora] = useState(false);
  const [hueco, setHueco] = useState("");
  // 42: «El cliente pide otra hora», el día y la hora de su información (D8) y la nota tras «Se hizo».
  const [pidiendoHora, setPidiendoHora] = useState(false);
  const [textoHora, setTextoHora] = useState("");
  const [conD8, setConD8] = useState(false);
  const [d8, setD8] = useState("");
  const [anotando, setAnotando] = useState(false);
  const [textoNota, setTextoNota] = useState("");
  const [canalNota, setCanalNota] = useState<string>("whatsapp");

  const visible = con36(negocio) && negocio.embudo === "ventas";
  const proyectoId = negocio.proyecto_id;
  const clave = ["nevora", "consultores", String(proyectoId)];
  const { data: consultores = [] } = useQuery({
    queryKey: clave,
    queryFn: () => dataProvider.consultoresDe(proyectoId),
    enabled: visible && proyectoId != null,
  });
  const { data: personas = [] } = useQuery({
    queryKey: ["nevora", "personas-consultoria", String(proyectoId)],
    queryFn: () => dataProvider.personasConsultoria(proyectoId),
    enabled: visible && proyectoId != null,
  });
  const enConsultoria = EN_CONSULTORIA.includes(negocio.etapa);
  const tiene42 = con42(negocio);
  const { data: meet } = useQuery({
    queryKey: ["nevora", "meet", String(proyectoId)],
    queryFn: () => dataProvider.meetConsultoria(proyectoId),
    enabled:
      visible &&
      tiene42 &&
      negocio.etapa === "consultoria_agendada" &&
      proyectoId != null,
  });
  const puedeElegir = enConsultoria && esAdmin;
  const soyConsultor = consultores.some((c) => String(c) === String(yo ?? "-"));
  const llevaLaConsultoria = tocable && (esAdmin || soyConsultor);
  const agendo = personas.find((p) => p.papel === "agendo");
  const nombreDe = (id: Identifier) =>
    personas.find(
      (p) => p.papel === "consultor" && String(p.sale_id) === String(id),
    )?.nombre ||
    nombre(id) ||
    "…";
  const { data: posibles = [] } = useQuery({
    queryKey: ["nevora", "consultores-posibles"],
    queryFn: () => dataProvider.consultoresPosibles(),
    enabled: visible && puedeElegir,
    staleTime: 5 * 60 * 1000,
  });

  if (!visible || (!enConsultoria && !consultores.length)) return null;

  const abrir = () => {
    setEleccion(consultores);
    setEditando(true);
  };
  const alternar = (id: Identifier) =>
    setEleccion((actual) =>
      actual.some((x) => String(x) === String(id))
        ? actual.filter((x) => String(x) !== String(id))
        : [...actual, id],
    );
  const guardar = async () => {
    setGuardando(true);
    try {
      await dataProvider.ponerConsultores(proyectoId, eleccion);
      await queryClient.invalidateQueries({ queryKey: clave });
      notify("nevora.ficha.consultores_guardados", { type: "success" });
      setEditando(false);
    } catch (e) {
      notify(e instanceof Error ? e.message : "ra.notification.http_error", {
        type: "error",
      });
    } finally {
      setGuardando(false);
    }
  };

  const confirmar = async (confirmada: boolean) => {
    try {
      await dataProvider.confirmarCita(proyectoId, confirmada);
      notify(
        confirmada
          ? "nevora.ficha.cita_confirmada_hecho"
          : "nevora.ficha.cita_desconfirmada_hecho",
        { type: "success" },
      );
      refresh();
    } catch (e) {
      notify(e instanceof Error ? e.message : "ra.notification.http_error", {
        type: "error",
      });
    }
  };

  const conConfirmar =
    negocio.etapa === "consultoria_agendada" &&
    "consultoria_confirmada_at" in negocio &&
    llevaLaConsultoria;
  const conResultado =
    negocio.etapa === "consultoria_agendada" && llevaLaConsultoria;
  const conCambiarHora =
    ajustes.agenda.propia &&
    negocio.etapa === "consultoria_agendada" &&
    tocable;

  const guardarHora = async () => {
    if (!hueco) return;
    setGuardando(true);
    try {
      await dataProvider.agendarConsultoria({
        negocio,
        canal: "otro",
        inicio: hueco,
      });
      await queryClient.invalidateQueries({ queryKey: ["nevora", "huecos"] });
      await queryClient.invalidateQueries({ queryKey: clave });
      notify("nevora.agenda_propia.hora_cambiada", { type: "success" });
      setCambiandoHora(false);
      setHueco("");
      refresh();
    } catch (e) {
      avisarError(e);
    } finally {
      setGuardando(false);
    }
  };

  const avisarError = (e: unknown) =>
    notify(e instanceof Error ? e.message : "ra.notification.http_error", {
      type: "error",
    });

  const sinRespuesta = async (marcar: boolean) => {
    try {
      await dataProvider.marcarSinRespuesta(proyectoId, marcar);
      notify(
        marcar
          ? "nevora.ficha.sin_respuesta_hecho"
          : "nevora.ficha.sin_respuesta_quitada",
        { type: "success" },
      );
      refresh();
    } catch (e) {
      avisarError(e);
    }
  };

  const pedirHora = async () => {
    if (!textoHora.trim()) return;
    setGuardando(true);
    try {
      await dataProvider.pedirOtraHora(proyectoId, textoHora.trim());
      notify("nevora.ficha.otra_hora_hecho", { type: "success" });
      setPidiendoHora(false);
      setTextoHora("");
      refresh();
    } catch (e) {
      avisarError(e);
    } finally {
      setGuardando(false);
    }
  };

  const anotar = async () => {
    if (!textoNota.trim()) return;
    setGuardando(true);
    try {
      await dataProvider.anotarConsultor(
        proyectoId,
        textoNota.trim(),
        canalNota,
      );
      notify("nevora.ficha.nota_consultor_hecha", { type: "success" });
      setAnotando(false);
      setTextoNota("");
      refresh();
    } catch (e) {
      avisarError(e);
    } finally {
      setGuardando(false);
    }
  };

  const copiarMeet = async () => {
    if (!meet) return;
    try {
      await navigator.clipboard.writeText(meet);
      notify("nevora.ficha.meet_copiado", { type: "info" });
    } catch {
      notify("nevora.ficha.meet_no_copiado", { type: "warning" });
    }
  };

  const seHizo = async (
    destino: Etapa,
    extras: Record<string, unknown> = {},
  ) => {
    setGuardando(true);
    try {
      await dataProvider.moverProyecto(negocio, destino, extras);
      notify("nevora.ficha.se_hizo_hecho", {
        type: "success",
        messageArgs: { etapa: translate(claveEtapa(destino)) },
      });
      setResultado(null);
      setConD8(false);
      setD8("");
      refresh();
    } catch (e) {
      avisarError(e);
    } finally {
      setGuardando(false);
    }
  };

  const noSePresento = async () => {
    setGuardando(true);
    try {
      // Como resultado de la consultoría (videollamada), no como llamada: no cuenta en el resumen.
      await dataProvider.registrarResultado({
        negocio,
        canal: "videollamada",
        resultado: "no_se_presento",
        datos: {
          fecha: new Date().toISOString(),
          fechaSiguientePaso: aFechaISO(new Date()),
          siguientePaso: translate("nevora.resultado.paso_no_show"),
        },
        ajustes,
      });
      notify("nevora.ficha.no_se_presento_hecho", { type: "success" });
      setResultado(null);
      refresh();
    } catch (e) {
      avisarError(e);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div
      className="rounded-lg border bg-card px-4 py-3 space-y-2"
      data-testid="consultoria-ficha"
    >
      <div className="flex flex-wrap items-center gap-2">
        <UsersRound className="size-4 text-muted-foreground" aria-hidden />
        <span className="text-sm font-medium">
          {translate("nevora.ficha.consultores")}:
        </span>
        <span className="text-sm" data-testid="consultores">
          {consultores.length
            ? consultores.map(nombreDe).join(", ")
            : translate("nevora.ficha.consultores_sin")}
        </span>
        {puedeElegir && !editando ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9 md:h-7"
            onClick={abrir}
          >
            {translate("nevora.ficha.consultores_cambiar")}
          </Button>
        ) : null}
        {conConfirmar ? (
          <Button
            type="button"
            size="sm"
            variant={negocio.consultoria_confirmada_at ? "outline" : "default"}
            className="h-9 md:h-7 ml-auto"
            title={translate("nevora.ficha.confirmar_cita_ayuda")}
            onClick={() => confirmar(!negocio.consultoria_confirmada_at)}
            data-testid="boton-confirmar-cita"
          >
            {negocio.consultoria_confirmada_at ? (
              <>
                <Ban className="size-4 mr-1" />
                {translate("nevora.ficha.desconfirmar_cita")}
              </>
            ) : (
              <>
                <CalendarCheck className="size-4 mr-1" />
                {translate("nevora.ficha.confirmar_cita")}
              </>
            )}
          </Button>
        ) : null}
        {conConfirmar && tiene42 && !negocio.consultoria_confirmada_at ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-9 md:h-7"
            title={translate("nevora.ficha.sin_respuesta_ayuda")}
            onClick={() => sinRespuesta(!negocio.consultoria_sin_respuesta_at)}
            data-testid="boton-sin-respuesta"
          >
            <PhoneMissed className="size-4 mr-1" />
            {negocio.consultoria_sin_respuesta_at
              ? translate("nevora.ficha.quitar_sin_respuesta")
              : translate("nevora.ficha.sin_respuesta")}
          </Button>
        ) : null}
      </div>
      {meet ? (
        <p
          className="flex flex-wrap items-center gap-2 text-sm"
          data-testid="meet"
        >
          <Video className="size-4 text-muted-foreground" aria-hidden />
          <span className="text-muted-foreground">
            {translate("nevora.ficha.meet")}:
          </span>
          <a
            href={meet}
            target="_blank"
            rel="noreferrer"
            className="text-[#3D8BD4] underline-offset-2 hover:underline break-all"
          >
            {meet}
          </a>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8"
            onClick={copiarMeet}
          >
            <Copy className="size-4 mr-1" />
            {translate("nevora.ficha.meet_copiar")}
          </Button>
        </p>
      ) : null}
      {tiene42 &&
      negocio.etapa === "esperando_info" &&
      negocio.fecha_info_cliente ? (
        <p className="text-sm" data-testid="info-cliente">
          <span className="text-muted-foreground">
            {translate("nevora.ficha.info_cliente")}:
          </span>{" "}
          {formatoFechaHora(negocio.fecha_info_cliente)}
        </p>
      ) : null}
      {conCambiarHora ? (
        cambiandoHora ? (
          <div className="space-y-2 border-t pt-2" data-testid="cambiar-hora">
            <SelectorHuecos valor={hueco} cambiar={setHueco} />
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                disabled={!hueco || guardando}
                onClick={guardarHora}
              >
                {translate("nevora.agenda_propia.guardar_hora")}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setCambiandoHora(false)}
              >
                {translate("nevora.ficha.consultores_cancelar")}
              </Button>
            </div>
          </div>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-9 md:h-8"
            onClick={() => setCambiandoHora(true)}
          >
            {translate("nevora.agenda_propia.cambiar_hora")}
          </Button>
        )
      ) : null}
      {agendo && String(agendo.sale_id) !== String(yo ?? "-") ? (
        <p className="text-sm" data-testid="agendada-por">
          <span className="text-muted-foreground">
            {translate("nevora.ficha.agendada_por")}:
          </span>{" "}
          {agendo.nombre}
          {agendo.email ? (
            <a
              href={"mailto:" + agendo.email}
              className="ml-2 inline-flex items-center gap-1 text-[#3D8BD4] underline-offset-2 hover:underline"
            >
              <Mail className="size-3.5" aria-hidden />
              {translate("nevora.ficha.escribir_a", { nombre: agendo.nombre })}
            </a>
          ) : null}
        </p>
      ) : null}
      {tiene42 &&
      negocio.etapa === "consultoria_agendada" &&
      soyConsultor &&
      !esAdmin ? (
        pidiendoHora ? (
          <div
            className="space-y-2 border-t pt-2"
            data-testid="pedir-otra-hora"
          >
            <Label htmlFor="texto-otra-hora">
              {translate("nevora.ficha.otra_hora_que")}
            </Label>
            <Textarea
              id="texto-otra-hora"
              rows={2}
              maxLength={500}
              value={textoHora}
              onChange={(e) => setTextoHora(e.target.value)}
            />
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                disabled={!textoHora.trim() || guardando}
                onClick={pedirHora}
              >
                {translate("nevora.ficha.otra_hora_enviar")}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setPidiendoHora(false)}
              >
                {translate("nevora.ficha.consultores_cancelar")}
              </Button>
            </div>
          </div>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-9 md:h-8"
            onClick={() => setPidiendoHora(true)}
          >
            <CalendarClock className="size-4 mr-1" />
            {translate("nevora.ficha.otra_hora")}
          </Button>
        )
      ) : null}
      {tiene42 &&
      soyConsultor &&
      !esAdmin &&
      negocio.consultoria_realizada_at ? (
        anotando ? (
          <div className="space-y-2 border-t pt-2" data-testid="nota-consultor">
            <Label htmlFor="texto-nota-consultor">
              {translate("nevora.ficha.nota_consultor")}
            </Label>
            <select
              aria-label={translate("nevora.ficha.nota_canal")}
              className="h-9 rounded-md border bg-background px-2 text-sm"
              value={canalNota}
              onChange={(e) => setCanalNota(e.target.value)}
            >
              {CANALES_NOTA.map((c) => (
                <option key={c} value={c}>
                  {translate(`nevora.ficha.nota_canal_${c}`)}
                </option>
              ))}
            </select>
            <Textarea
              id="texto-nota-consultor"
              rows={3}
              maxLength={4000}
              value={textoNota}
              onChange={(e) => setTextoNota(e.target.value)}
            />
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                disabled={!textoNota.trim() || guardando}
                onClick={anotar}
              >
                {translate("nevora.ficha.nota_consultor_guardar")}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setAnotando(false)}
              >
                {translate("nevora.ficha.consultores_cancelar")}
              </Button>
            </div>
          </div>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-9 md:h-8"
            onClick={() => setAnotando(true)}
          >
            <StickyNote className="size-4 mr-1" />
            {translate("nevora.ficha.nota_consultor")}
          </Button>
        )
      ) : null}
      {conResultado ? (
        <div
          className="space-y-2 border-t pt-2"
          data-testid="resultado-consultoria"
        >
          <p className="text-sm font-medium">
            {translate("nevora.ficha.resultado_consultoria")}
          </p>
          {resultado === null ? (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-9 md:h-8"
                onClick={() => setResultado("se_hizo")}
              >
                <CircleCheck className="size-4 mr-1" />
                {translate("nevora.ficha.consultoria_se_hizo")}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-9 md:h-8"
                onClick={() => setResultado("no_se_presento")}
              >
                <UserX className="size-4 mr-1" />
                {translate("nevora.ficha.consultoria_no_se_presento")}
              </Button>
            </div>
          ) : resultado === "se_hizo" ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                {translate("nevora.ficha.se_hizo_pregunta")}
              </p>
              <div className="flex flex-wrap gap-2">
                {TRAS_CONSULTORIA.map((e, i) => (
                  <Button
                    key={e}
                    type="button"
                    size="sm"
                    variant={i === 0 ? "default" : "outline"}
                    disabled={guardando}
                    onClick={() =>
                      tiene42 && e === "esperando_info"
                        ? setConD8(true)
                        : seHizo(e)
                    }
                  >
                    {translate(claveEtapa(e))}
                  </Button>
                ))}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setResultado(null)}
                >
                  {translate("nevora.ficha.consultores_cancelar")}
                </Button>
              </div>
              {conD8 ? (
                <div className="space-y-1" data-testid="d8">
                  <Label htmlFor="fecha-info-cliente">
                    {translate("nevora.ficha.d8_pregunta")}
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    <Input
                      id="fecha-info-cliente"
                      type="datetime-local"
                      className="w-auto"
                      value={d8}
                      onChange={(ev) => setD8(ev.target.value)}
                    />
                    <Button
                      type="button"
                      size="sm"
                      disabled={!deDatetimeLocal(d8) || guardando}
                      onClick={() =>
                        seHizo("esperando_info", {
                          fecha_info_cliente: deDatetimeLocal(d8),
                        })
                      }
                    >
                      {translate("nevora.ficha.d8_guardar")}
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                disabled={guardando}
                onClick={noSePresento}
                data-testid="confirmar-no-se-presento"
              >
                <UserX className="size-4 mr-1" />
                {translate("nevora.ficha.consultoria_no_se_presento")}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setResultado(null)}
              >
                {translate("nevora.ficha.consultores_cancelar")}
              </Button>
            </div>
          )}
        </div>
      ) : null}
      {editando ? (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            {translate("nevora.ficha.consultores_ayuda")}
          </p>
          <div className="flex flex-wrap gap-2">
            {posibles.map((p) => {
              const pos = eleccion.findIndex((x) => String(x) === String(p.id));
              return (
                <button
                  key={String(p.id)}
                  type="button"
                  aria-pressed={pos >= 0}
                  onClick={() => alternar(p.id)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors",
                    pos >= 0
                      ? "border-foreground bg-accent font-medium"
                      : "border-border bg-card hover:bg-muted",
                  )}
                >
                  {pos >= 0 ? (
                    <span className="flex size-4 items-center justify-center rounded-full bg-foreground text-[10px] text-background">
                      {pos + 1}
                    </span>
                  ) : null}
                  {p.nombre}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              disabled={guardando}
              onClick={guardar}
            >
              <Check className="size-4 mr-1" />
              {translate("nevora.ficha.consultores_guardar")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setEditando(false)}
            >
              {translate("nevora.ficha.consultores_cancelar")}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Por qué y desde cuándo la ficha está en la lista de no contactar (36): las entradas de
 * public.no_contactar de este negocio. Sin entradas (o sin permiso para verlas), nada.
 */
export const NoContactarFicha = ({ negocio }: { negocio: Negocio }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const { data: entradas = [] } = useQuery({
    queryKey: ["nevora", "no-contactar", String(negocio.negocio_id)],
    queryFn: () => dataProvider.noContactarDe(negocio.negocio_id),
    enabled: con36(negocio) && negocio.negocio_id != null,
  });
  if (!entradas.length) return null;
  return (
    <div
      className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm dark:border-red-900 dark:bg-red-950/40"
      data-testid="no-contactar"
    >
      <p className="font-semibold text-red-800 dark:text-red-300">
        {translate("nevora.ficha.no_contactar_titulo")}
      </p>
      <ul className="mt-1 space-y-0.5">
        {entradas.map((e, i) => (
          <li key={`${e.fecha}-${i}`}>
            {translate("nevora.ficha.no_contactar_desde", {
              fecha: formatoFecha(e.fecha),
              motivo: e.motivo?.trim()
                ? e.motivo
                : translate("nevora.ficha.no_contactar_sin_motivo"),
            })}{" "}
            <span className="text-muted-foreground">
              (
              {translate(
                e.app_origen === "sales" || e.app_origen === "crm"
                  ? "nevora.ficha.no_contactar_crm"
                  : "nevora.ficha.no_contactar_prospect",
              )}
              )
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
